import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';

export interface SearchBarProps {
  /** Current applied (debounced) value — the source of truth from the parent. */
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  ariaLabel: string;
  /** Debounce (ms) before firing onChange. Defaults to 150ms. */
  debounceMs?: number;
}

export function SearchBar({
  value,
  onChange,
  placeholder,
  ariaLabel,
  debounceMs = 150,
}: SearchBarProps) {
  const [draft, setDraft] = useState(value);

  // Keep local draft in sync if the parent resets the value (e.g. clear).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const id = window.setTimeout(() => onChange(draft), debounceMs);
    return () => window.clearTimeout(id);
  }, [draft, value, debounceMs, onChange]);

  return (
    <label className="aw-search">
      <Search size={16} className="aw-search__icon" aria-hidden="true" />
      <input
        type="search"
        className="aw-search__input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        spellCheck={false}
        autoComplete="off"
      />
      {draft && (
        <button
          type="button"
          className="aw-search__clear"
          onClick={() => setDraft('')}
          aria-label="Clear search"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </label>
  );
}
